// 004 (FR-027, FR-029, FR-038; BR-1; data-model §6.3): the pin picker's state. The pin counts as
// placed only after the parent drags/taps/pans the map, uses current location or picks a search
// result; programmatic moves never place it. The map fails after 10 s without ready or 8 tile errors.
import type { LatLng, PlaceAddress } from "./types";

export const READY_TIMEOUT_MS = 10_000;
export const TILE_FAILURES = 8;

export type PinState = {
  placed: boolean;
  center: LatLng | null;
  description: PlaceAddress | null;
  map: "loading" | "ready" | "failed";
  tileErrors: number;
  /** Bumped on retry so the map remounts. */
  attempt: number;
};

export type PinEvent =
  | { type: "moved"; center: LatLng; byUser: boolean }
  | { type: "located"; center: LatLng }
  | { type: "picked"; center: LatLng; description?: PlaceAddress | null }
  | { type: "described"; description: PlaceAddress | null }
  | { type: "ready" }
  | { type: "tileError" }
  | { type: "timeout" }
  | { type: "retry" };

export function initialPinState(saved?: LatLng): PinState {
  return {
    placed: saved !== undefined,
    center: saved ?? null,
    description: null,
    map: "loading",
    tileErrors: 0,
    attempt: 0,
  };
}

export function pinReducer(s: PinState, e: PinEvent): PinState {
  switch (e.type) {
    case "moved":
      return e.byUser
        ? { ...s, placed: true, center: e.center, description: null }
        : { ...s, center: e.center };
    case "located":
      return { ...s, placed: true, center: e.center, description: null };
    case "picked":
      return {
        ...s,
        placed: true,
        center: e.center,
        description: e.description ?? null,
      };
    case "described":
      return { ...s, description: e.description };
    case "ready":
      return { ...s, map: "ready" };
    case "tileError": {
      const tileErrors = s.tileErrors + 1;
      return {
        ...s,
        tileErrors,
        map: tileErrors >= TILE_FAILURES ? "failed" : s.map,
      };
    }
    case "timeout":
      return s.map === "loading" ? { ...s, map: "failed" } : s;
    case "retry":
      return { ...s, map: "loading", tileErrors: 0, attempt: s.attempt + 1 };
  }
}
